import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Avatar,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Icon,
} from "@chakra-ui/react";
import { Camera } from "lucide-react";

const ProfilePictureCropper: React.FC = () => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const CONTAINER_SIZE = 400;
  const CROP_SIZE = 300;

  const drawPreview = useCallback(() => {
    if (!imageSrc || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const img = new Image();
    img.onload = () => {
      const scale = Math.min(CONTAINER_SIZE / img.width, CONTAINER_SIZE / img.height);
      const scaledWidth = img.width * scale * zoom;
      const scaledHeight = img.height * scale * zoom;

      const x = CONTAINER_SIZE / 2 + position.x;
      const y = CONTAINER_SIZE / 2 + position.y;

      ctx.save();
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
      ctx.fillRect(0, 0, CONTAINER_SIZE, CONTAINER_SIZE);

      ctx.globalCompositeOperation = "source-over";
      ctx.beginPath();
      ctx.arc(CONTAINER_SIZE / 2, CONTAINER_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalCompositeOperation = "source-over";
      ctx.drawImage(img, x - scaledWidth / 2, y - scaledHeight / 2, scaledWidth, scaledHeight);

      ctx.strokeStyle = "white";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(CONTAINER_SIZE / 2, CONTAINER_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    };

    img.src = imageSrc;
  }, [imageSrc, zoom, position]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imgData = event.target?.result as string;
        setImageSrc(imgData);
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        onOpen();
      };
      reader.readAsDataURL(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (isOpen && imageSrc) {
      const timer = setTimeout(() => {
        drawPreview();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, imageSrc, drawPreview]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left - position.x,
      y: e.clientY - rect.top - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const newX = e.clientX - rect.left - dragStart.x;
    const newY = e.clientY - rect.top - dragStart.y;
    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const getCroppedImage = useCallback(() => {
    if (!imageSrc || !canvasRef.current) return;

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = CROP_SIZE;
      canvas.height = CROP_SIZE;

      const scale = Math.min(CONTAINER_SIZE / img.width, CONTAINER_SIZE / img.height);
      const scaledWidth = img.width * scale * zoom;
      const scaledHeight = img.height * scale * zoom;

      const centerX = CONTAINER_SIZE / 2 + position.x;
      const centerY = CONTAINER_SIZE / 2 + position.y;

      const cropX = CONTAINER_SIZE / 2 - CROP_SIZE / 2;
      const cropY = CONTAINER_SIZE / 2 - CROP_SIZE / 2;

      const sourceX = (cropX - (centerX - scaledWidth / 2)) / (scale * zoom);
      const sourceY = (cropY - (centerY - scaledHeight / 2)) / (scale * zoom);
      const sourceSize = CROP_SIZE / (scale * zoom);

      ctx.save();
      ctx.beginPath();
      ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      ctx.drawImage(img, sourceX, sourceY, sourceSize, sourceSize, 0, 0, CROP_SIZE, CROP_SIZE);
      ctx.restore();

      const croppedDataUrl = canvas.toDataURL("image/png", 1);
      setCroppedImage(croppedDataUrl);
      onClose();
    };

    img.src = imageSrc;
  }, [imageSrc, zoom, position, onClose]);

  const handleUpload = () => {
    if (croppedImage) {
      console.log("Uploading image:", croppedImage);
      fetch(croppedImage)
        .then((res) => res.blob())
        .then((blob) => {
          console.log("Blob size:", blob.size, "bytes");
          console.log("Blob type:", blob.type);
          alert("Image ready for upload! Check console for details.");
        });
    }
  };

  const handleModalClose = () => {
    onClose();
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <Box>
      <VStack spacing={8} maxW="600px" mx="auto" px={4}>
        <Text fontSize="3xl" fontWeight="bold"></Text>

        <VStack spacing={4}>
          <Box position="relative">
            <Avatar size="xl" src={croppedImage || "/assets/img/profile.jpg"} bg="white" icon={<Icon as={Camera} boxSize={8} />} />
            <Button position="absolute" bottom={0} right={0} size="sm" bg="red.500" color="white" borderRadius="full" onClick={() => fileInputRef.current?.click()}>
              <Icon as={Camera} size={12} />
            </Button>
          </Box>

          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: "none" }} />
        </VStack>

        <Modal isOpen={isOpen} onClose={handleModalClose} size="xl" closeOnOverlayClick={false}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Crop Your Photo</ModalHeader>
            <ModalBody>
              <VStack spacing={6}>
                <Box borderRadius="md" overflow="hidden" bg="gray.800" cursor={isDragging ? "grabbing" : "grab"} position="relative" width={`${CONTAINER_SIZE}px`} height={`${CONTAINER_SIZE}px`}>
                  <canvas
                    ref={previewCanvasRef}
                    width={CONTAINER_SIZE}
                    height={CONTAINER_SIZE}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Box>

                <Box width="100%">
                  <Text mb={2} fontWeight="semibold">
                    Zoom: {zoom.toFixed(1)}x
                  </Text>
                  <Slider
                    value={zoom}
                    onChange={(value) => {
                      setZoom(value);
                    }}
                    min={0.5}
                    max={3}
                    step={0.1}
                  >
                    <SliderTrack>
                      <SliderFilledTrack bg="blue.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={6} />
                  </Slider>
                </Box>

                <Text fontSize="sm" color="gray.600">
                  Drag to reposition • Use slider to zoom
                </Text>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <HStack spacing={3}>
                <Button variant="ghost" onClick={handleModalClose}>
                  Cancel
                </Button>
                <Button colorScheme="blue" onClick={getCroppedImage}>
                  Crop & Save
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <canvas ref={canvasRef} style={{ display: "none" }} />
      </VStack>
    </Box>
  );
};

export default ProfilePictureCropper;
